// src/redux/chatSlice.js

import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import ChatService from "../../services/api/chatService";
import {
  addDateAndTime,
  unauthorizedError,
  getCurrentDate,
} from "../../utils/helpers";
import { findChatSearchMatches } from "../../utils/chatSearch";
import { toast } from "react-toastify";
import { setSessionExpired } from "./globalSlice";

const chat = new ChatService();
// Earliest date for chat history (fixed); endDate must be computed at request time
// so that after re-login or new day the API fetches up to "today" and no chats disappear.
const DEFAULT_START_DATE = "2025-01-15";

export const getChatData = createAsyncThunk(
  `chat/getChatData`,
  async ({ page, navigate }, { dispatch }) => {
    try {
      dispatch(setLoading(true));
      const endDate = getCurrentDate();
      const response = await chat.getChatData(
        DEFAULT_START_DATE,
        endDate,
        page,
        navigate
      );
      dispatch(setLoading(false));
      if (response) {
        // New shape: { chats: [...], pageinfo: {...} }.
        // Legacy shape: [...messages, { pageinfo }] kept as a fallback.
        const isLegacy = Array.isArray(response);
        const chats = isLegacy
          ? response.filter((item) => !item.pageinfo)
          : response.chats || [];
        const pageInfo = isLegacy
          ? response.find((item) => item.pageinfo)?.pageinfo || null
          : response.pageinfo || null;
        if (pageInfo) {
          dispatch(
            setHasMorePages(
              parseInt(pageInfo.currentPage) < parseInt(pageInfo.totalPages)
            )
          );
        }
        const linkUrl = addDateAndTime(chats);
        dispatch(setDateTime(linkUrl));
        return chats;
      } else {
        throw new Error();
      }
    } catch (error) {
      if (error?.response) {
        unauthorizedError(navigate);
        dispatch(setSessionExpired(true));
      } else {
      }
      dispatch(setLoading(false));
      throw error;
    }
  }
);
export const getfeedbackData = createAsyncThunk(
  `/feedback`,
  async ({ body, navigate, id }, { dispatch }) => {
    try {
      const response = await chat.feedback(body, navigate, id);
      toast.success(response?.message);
      if (response) {
        const linkUrl = addDateAndTime(response);
        dispatch(setDateTime(linkUrl));

        return response;
      } else {
        throw new Error();
      }
    } catch (error) {
      if (error?.response) {
        unauthorizedError(navigate);
        dispatch(setSessionExpired(true));
      } else {
        toast.error(error.response.message);
      }
      dispatch(setLoading(false));
      throw error;
    }
  }
);
export const upload_image_regignation = createAsyncThunk(
  `/uploadImage`,
  async ({ body, navigate, sendMessage }, { dispatch }) => {
    try {
      toast.success("Document uploading....");
      const response = await chat.uploadImageRegignation(body, navigate);
      sendMessage(response, "text");
      dispatch(setUploadImage(response));
      if (response) {
        const linkUrl = addDateAndTime(response);
        dispatch(setDateTime(linkUrl));
        return response;
      } else {
        throw new Error();
      }
    } catch (error) {
      if (error?.response) {
        unauthorizedError(navigate);
        dispatch(setSessionExpired(true));
      } else {
        toast.error(error.response.message);
      }
      dispatch(setLoading(false));
      throw error;
    }
  }
);

const chatSlice = createSlice({
  name: "chat",
  initialState: {
    loading: false,
    messages: [],
    newMessage: "",
    socket: {
      connected: false,
    },
    dateData: null,
    review: false,
    jobId: "",
    feedbackStatus: "",
    feedbackLoading: false,
    isThumbsUp: true,
    isThumbsDown: true,
    isReviewIcon: false,
    feedbackSubmitted: false,
    reviewSubmitted: false,
    loader: true,
    uploadImage: "",
    isSuggestions: false,
    botButtons: [],
    botType: "",
    askForFeedback: false,
    shouldScrollSuggestions: false,
    showSuggestions: false,
    suggestions: [],
    showScrollIcon: false,
    chatPage: 1,
    searchTerm: "",
    searchIndex: 0,
    searchMatches: [],
    isSearchLoading: false,
    latestActionCancellable: false,
    hasMorePages: true,
    isChatCleared: false,
  },
  reducers: {
    setNewMessage: (state, action) => {
      state.newMessage = action.payload;
    },
    setSocket: (state, action) => {
      state.socket = {
        connected: true,
      };
    },
    setMessages: (state, action) => {
      state.messages.push(action.payload);
      state.latestActionCancellable = !!action.payload?.cancellable;
    },
    setHasMorePages: (state, action) => {
      state.hasMorePages = action.payload;
    },
    setClearedMessages: (state, action) => {
      // state.messages = action.payload;
      const uniqueMessages = [];
      const seenIds = new Set();
      const messageArray = Array.isArray(action.payload) ? action.payload : [];
      messageArray.forEach((msg) => {
        if (msg._id && !seenIds.has(msg._id)) {
          seenIds.add(msg._id);
          uniqueMessages.push(msg);
        } else if (!msg._id) {
          uniqueMessages.push(msg);
        }
      });
      state.messages = uniqueMessages;
    },
    setDateTime: (state, action) => {
      state.dateData = action.payload;
    },
    setReview: (state, action) => {
      state.review = action.payload;
    },
    setJobId: (state, action) => {
      state.jobId = action.payload;
    },
    setFeedbackStatus: (state, action) => {
      state.feedbackStatus = action.payload;
    },
    setIsThumbsUp: (state, action) => {
      state.isThumbsUp = action.payload;
    },
    setIsThumbsDown: (state, action) => {
      state.isThumbsDown = action.payload;
    },
    setIsReviewIcon: (state, action) => {
      state.isReviewIcon = action.payload;
    },
    setFeedbackSubmitted: (state, action) => {
      state.feedbackSubmitted = action.payload;
    },
    setReviewSubmitted: (state, action) => {
      state.reviewSubmitted = action.payload;
    },
    setLoading: (state, action) => {
      state.loader = action.payload;
    },
    setUploadImage: (state, action) => {
      state.uploadImage = action.payload;
    },
    setIsSuggestions: (state, action) => {
      state.isSuggestions = action.payload;
    },
    setBotButons: (state, action) => {
      state.botButtons = action.payload;
    },
    setBotType: (state, action) => {
      state.botType = action.payload;
    },
    setAskForFeedback: (state, action) => {
      state.askForFeedback = action.payload;
    },
    setShouldScrollSuggestions: (state, action) => {
      state.shouldScrollSuggestions = action.payload;
    },
    setShowSuggestions: (state, action) => {
      state.showSuggestions = action.payload;
    },
    setSuggestions: (state, action) => {
      state.suggestions = action.payload;
    },
    setShowScrollIcon: (state, action) => {
      state.showScrollIcon = action.payload;
    },
    setChatPage: (state, action) => {
      state.chatPage = action.payload;
    },
    setChatClearedStatus: (state, action) => {
      state.isChatCleared = action.payload;
    },
    setChatSearchResults: (state, action) => {
      const { searchTerm, matches, activeIndex } = action.payload;
      state.searchTerm = searchTerm;
      state.searchMatches = matches;
      if (typeof activeIndex === "number" && matches.length) {
        const clampedIndex = Math.min(
          Math.max(activeIndex, 0),
          matches.length - 1
        );
        state.searchIndex = clampedIndex;
      } else {
        state.searchIndex = matches.length ? 0 : 0;
      }
    },
    addChatSearchResults: (state, action) => {
      const { newMatches } = action.payload;
      if (!Array.isArray(newMatches) || newMatches.length === 0) {
        return;
      }
      const existingIds = new Set(
        state.searchMatches.map((m) => m.messageId || m.fallbackIndex)
      );
      const uniqueNewMatches = newMatches.filter(
        (m) => !existingIds.has(m.messageId || m.fallbackIndex)
      );
      const allMatches = [...state.searchMatches, ...uniqueNewMatches].sort(
        (a, b) => {
          if (a.timestamp && b.timestamp) {
            return new Date(b.timestamp) - new Date(a.timestamp);
          }
          return (b.fallbackIndex || 0) - (a.fallbackIndex || 0);
        }
      );
      state.searchMatches = allMatches;
      if (state.searchIndex >= allMatches.length) {
        state.searchIndex = Math.max(0, allMatches.length - 1);
      }
    },
    setChatSearchIndex: (state, action) => {
      const requestedIndex = action.payload;
      if (!state.searchMatches.length) {
        state.searchIndex = 0;
        return;
      }
      const clampedIndex = Math.min(
        Math.max(requestedIndex, 0),
        state.searchMatches.length - 1
      );
      state.searchIndex = clampedIndex;
    },
    setIsSearchLoading: (state, action) => {
      state.isSearchLoading = action.payload;
    },
    clearMessages: (state) => {
      //TODO maybe we need to also cancel previous jobs while erasing the screen
      state.messages = [];
      state.hasMorePages = true;
      state.isChatCleared = true;
    },
    resetChatSearch: (state) => {
      state.isSearchLoading = false;
      state.searchMatches = [];
      state.searchTerm = "";
      state.searchIndex = 0;
      state.chatPage = 1;
      state.isChatCleared = false;
    },
    restoreMessages: (state) => {
      state.isChatCleared = false;
      state.chatPage = 1;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getChatData.pending, (state) => {
        state.loading = true;
      })
      .addCase(getChatData.fulfilled, (state, action) => {
        state.loading = false;
        const newMessages = Array.isArray(action.payload)
          ? action.payload
          : [action.payload];
        const messagesOnly = newMessages.filter((item) => !item.pageinfo);
        const reversedNewMessages = [...messagesOnly].reverse();
        const existingIds = new Set(state.messages.map((msg) => msg._id));
        const filteredNewMessages = reversedNewMessages.filter(
          (msg) => msg._id && !existingIds.has(msg._id)
        );
        // state.messages = [...filteredNewMessages, ...state.messages];
        // state.messages = reversedNewMessages;
        // state.messages = [...reversedNewMessages, ...state.messages];
        if (state.chatPage === 1 || state.messages.length === 0) {
          state.messages = reversedNewMessages;
        } else {
          state.messages = [...filteredNewMessages, ...state.messages];
        }
        if (state.searchTerm?.trim()) {
          const normalizedTerm = state.searchTerm.trim().toLowerCase();
          const previousMatches = state.searchMatches || [];
          const previousIndex = state.searchIndex || 0;
          const previousActiveMatch =
            previousMatches.length > 0 ? previousMatches[previousIndex] : null;
          const recalculatedMatches = findChatSearchMatches(
            state.messages,
            normalizedTerm
          );
          let nextIndex = 0;
          if (previousActiveMatch && recalculatedMatches.length) {
            const retainedIndex = recalculatedMatches.findIndex((match) => {
              if (match.messageId && previousActiveMatch.messageId) {
                return match.messageId === previousActiveMatch.messageId;
              }
              return match.fallbackIndex === previousActiveMatch.fallbackIndex;
            });
            if (retainedIndex !== -1) {
              nextIndex = retainedIndex;
            } else {
              nextIndex = Math.min(
                previousIndex,
                recalculatedMatches.length - 1
              );
            }
          }
          state.searchMatches = recalculatedMatches;
          state.searchIndex = recalculatedMatches.length
            ? Math.max(Math.min(nextIndex, recalculatedMatches.length - 1), 0)
            : 0;
        }
      })
      .addCase(getChatData.rejected, (state) => {
        state.loading = false;
      })
      .addCase(getfeedbackData.pending, (state) => {
        state.feedbackLoading = true;
      })
      .addCase(getfeedbackData.fulfilled, (state, action) => {
        state.feedbackLoading = false;
        state.messages = action.payload;
      })
      .addCase(getfeedbackData.rejected, (state) => {
        state.feedbackLoading = false;
      })
      .addCase(upload_image_regignation.pending, (state) => {
        state.loader = true;
      })
      .addCase(upload_image_regignation.fulfilled, (state, action) => {
        state.loader = false;
        state.messages = action.payload;
      })
      .addCase(upload_image_regignation.rejected, (state) => {
        state.loader = false;
      });
  },
});

export const {
  setMessages,
  setClearedMessages,
  setNewMessage,
  setSocket,
  setDateTime,
  setReview,
  setJobId,
  setFeedbackStatus,
  setIsThumbsUp,
  setIsReviewIcon,
  setFeedbackSubmitted,
  setIsThumbsDown,
  setReviewSubmitted,
  setLoading,
  setUploadImage,
  setIsSuggestions,
  setBotButons,
  setBotType,
  setAskForFeedback,
  setShouldScrollSuggestions,
  setShowSuggestions,
  setSuggestions,
  setShowScrollIcon,
  setChatPage,
  clearMessages,
  resetChatSearch,
  setHasMorePages,
  restoreMessages,
  setChatClearedStatus,
  setChatSearchResults,
  setChatSearchIndex,
  addChatSearchResults,
  setIsSearchLoading,
} = chatSlice.actions;

export default chatSlice.reducer;
